import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  startClientApiInactivityTimerAction,
  stopClientApiInactivityTimerAction,
  clientApiUserActivityAction,
} from '../actionCreators';

const ACTIVITY_EVENTS = ['click', 'keydown', 'touchstart', 'change'];

// Nothing inside the iframe scrolls with dynamic height on: the widget grows to
// fit and the host page scrolls instead, which no in-iframe listener can see.
const SCROLL_EVENT = 'scroll';

/**
 * Ends an OLB journey the user has walked away from.
 *
 * @param {boolean} shouldRun - whether the user is on a journey the timer applies to.
 */
const useClientApiInactivityTimer = ({ shouldRun }) => {
  const dispatch = useDispatch();
  const [isOnline, setIsOnline] = useState(() => window.navigator.onLine);

  const isDynamicHeightEnabled = useSelector(
    (store) => store.optionalFormConfig?.isDynamicHeightEnabled
  );
  const isInactivityPromptOpen = useSelector(
    (store) => store.clientApiJourney?.isInactivityPromptOpen
  );

  // A user with no connection reads as idle whatever they do, so the timer would
  // end a journey they have not abandoned.
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Kept apart from the listener effect: re-running this when the prompt opens
  // would stop the timer mid-countdown.
  useEffect(() => {
    if (!shouldRun || !isOnline) return undefined;

    dispatch(startClientApiInactivityTimerAction());

    return () => {
      dispatch(stopClientApiInactivityTimerAction());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldRun, isOnline]);

  useEffect(() => {
    if (!shouldRun || isInactivityPromptOpen) return undefined;

    const reportActivity = () => dispatch(clientApiUserActivityAction());

    const events = isDynamicHeightEnabled
      ? ACTIVITY_EVENTS
      : [...ACTIVITY_EVENTS, SCROLL_EVENT];

    const options = { passive: true, capture: true };

    events.forEach((event) =>
      document.addEventListener(event, reportActivity, options)
    );

    return () => {
      events.forEach((event) =>
        document.removeEventListener(event, reportActivity, options)
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldRun, isInactivityPromptOpen, isDynamicHeightEnabled]);
};

export default useClientApiInactivityTimer;
